import { useState, useEffect, useMemo } from "react";
import { Check, Loader2, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import NumericInput from "@/components/NumericInput";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";

import { pdfjsLib, getPdfDocument } from "@/lib/pdfjs";
import { formatNumber, parseNumericLike } from "@/lib/numberFormatting";

export interface VatLineDraft {
  taxableBase: string;
  vatRate: string;
  vatAmount: string;
  expenseAccount?: string;
}


export interface FailedFile {
  id: string;
  file: File;
  reason: string;
  /**
   * "rejected" = el documento no era una factura (rechazo correcto, no penaliza la tasa de éxito).
   * "error" (por defecto) = fallo real del sistema (error servidor, faltan datos, etc.).
   */
  kind?: "rejected" | "error";
  editing?: boolean;
  editDate?: string;
  editSupplier?: string;
  editInvoiceNumber?: string;
  editSupplierTaxId?: string;
  /** Legacy single VAT line (compatibilidad con el formulario inline). */
  editTaxableBase?: string;
  editVatRate?: string;
  editVatAmount?: string;
  editIrpfRate?: string;
  editIrpfAmount?: string;
  editTotalAmount?: string;
  editPaymentMethod?: string;
  editSupplierAccount?: string;
  editDescription?: string;

}

interface FailedFileSplitViewProps {
  file: FailedFile | null;
  open: boolean;
  onClose: () => void;
  onFieldChange: (id: string, field: string, value: string) => void;
  /**
   * Se invoca al pulsar "Validar". El segundo parámetro son las líneas de IVA
   * introducidas (puede haber varias, una por tipo). Si no hay base/cuota se
   * descarta antes de propagar al ProcessedFile.
   */
  onValidate: (file: FailedFile, vatLines: VatLineDraft[]) => void;
  /** Envía el documento a la papelera y cierra el visor. */
  onDiscard?: (fileId: string) => void;
}

const emptyVatLine = (rate = "21"): VatLineDraft => ({
  taxableBase: "",
  vatRate: rate,
  vatAmount: "",
});

const FailedFileSplitView = ({
  file: ff,
  open,
  onClose,
  onFieldChange,
  onValidate,
  onDiscard,
}: FailedFileSplitViewProps) => {
  const [pdfPages, setPdfPages] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [vatLines, setVatLines] = useState<VatLineDraft[]>([emptyVatLine()]);

  // Inicializa las líneas de IVA cuando se abre el archivo. Si venía un valor
  // legacy de las celdas inline, se usa como primera línea.
  useEffect(() => {
    if (!ff || !open) return;
    const initial: VatLineDraft = {
      taxableBase: ff.editTaxableBase || "",
      vatRate: ff.editVatRate || "21",
      vatAmount: ff.editVatAmount || "",
    };
    setVatLines([initial]);
  }, [ff?.id, open]);

  useEffect(() => {
    if (!ff || !open) {
      setPdfPages([]);
      setImageUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    const isImage =
      ff.file.type === "image/png" ||
      ff.file.type === "image/jpeg" ||
      /\.(png|jpe?g)$/i.test(ff.file.name);

    // Limpiamos siempre el documento anterior antes de pintar el nuevo.
    setPdfPages([]);
    setImageUrl(null);

    if (isImage) {
      setLoading(false);
      objectUrl = URL.createObjectURL(ff.file);
      setImageUrl(objectUrl);
    } else {
      setLoading(true);
      (async () => {
        try {
          const buf = await ff.file.arrayBuffer();
          if (cancelled) return;
          const pdf = await (await getPdfDocument({ data: buf })).promise;
          const pages: string[] = [];
          for (let i = 1; i <= pdf.numPages; i++) {
            if (cancelled) return;
            const page = await pdf.getPage(i);
            const vp = page.getViewport({ scale: 1.5 });
            const canvas = document.createElement("canvas");
            canvas.width = vp.width;
            canvas.height = vp.height;
            await page.render({
              canvas, canvasContext: canvas.getContext("2d")!,
              viewport: vp,
            }).promise;
            pages.push(canvas.toDataURL("image/png"));
            canvas.width = 0;
            canvas.height = 0;
          }
          if (!cancelled) setPdfPages(pages);
        } catch (e) {
          console.error("Error rendering PDF in split view:", e);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ff?.id, open]);

  const totalsPreview = useMemo(() => {
    let base = 0;
    let vat = 0;
    for (const ln of vatLines) {
      base += parseNumericLike(ln.taxableBase);
      vat += parseNumericLike(ln.vatAmount);
    }
    return { base, vat };
  }, [vatLines]);

  if (!ff) return null;

  const fieldChange = (field: string, value: string) =>
    onFieldChange(ff.id, field, value);

  const updateVatLine = (idx: number, patch: Partial<VatLineDraft>) => {
    setVatLines((prev) =>
      prev.map((ln, i) => {
        if (i !== idx) return ln;

        const next = { ...ln, ...patch };
        const shouldRecalculate =
          Object.prototype.hasOwnProperty.call(patch, "taxableBase") ||
          Object.prototype.hasOwnProperty.call(patch, "vatRate");

        if (!shouldRecalculate) return next;

        const base = parseNumericLike(next.taxableBase);
        const rate = parseNumericLike(next.vatRate);
        if (!Number.isFinite(base) || !Number.isFinite(rate) || !next.taxableBase || !next.vatRate) {
          return { ...next, vatAmount: "" };
        }

        return { ...next, vatAmount: ((base * rate) / 100).toFixed(2) };
      }),
    );
  };

  const addVatLine = () => {
    setVatLines((prev) => [...prev, emptyVatLine(prev[prev.length - 1]?.vatRate || "21")]);
  };

  const removeVatLine = (idx: number) => {
    setVatLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[90vw] w-[90vw] h-[90vh] max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <DialogTitle className="truncate text-sm">
              {ff.file.name} — Corrección manual
            </DialogTitle>
            {pdfPages.length > 0 && (
              <span className="shrink-0 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {pdfPages.length} {pdfPages.length === 1 ? "página" : "páginas"}
              </span>
            )}
          </div>
          <DialogDescription className="sr-only">
            Revisión manual de un documento que no pudo procesarse automáticamente.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-hidden min-h-0">
          <ResizablePanelGroup direction="horizontal" className="h-full">
            <ResizablePanel defaultSize={55} minSize={25}>
              <div className="h-full overflow-y-auto p-4 space-y-3">
                {loading && (
                  <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Cargando documento…
                  </div>
                )}
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt={ff.file.name}
                    className="w-full rounded-lg border"
                  />
                )}
                {pdfPages.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`Página ${i + 1}`}
                    className="w-full rounded-lg border"
                  />
                ))}
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={45} minSize={25}>
              <div className="h-full overflow-y-auto p-4 space-y-4">
                <p className="text-xs text-destructive font-medium rounded-md bg-destructive/10 px-3 py-2">
                  {ff.reason}
                </p>

                {/* Required fields */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Datos obligatorios
                  </h3>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Fecha *
                      </label>
                      <Input
                        value={ff.editDate || ""}
                        onChange={(e) => fieldChange("editDate", e.target.value)}
                        placeholder="YYYY-MM-DD"
                        className="h-8 text-xs"
                        autoFocus
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Proveedor *
                      </label>
                      <Input
                        value={ff.editSupplier || ""}
                        onChange={(e) =>
                          fieldChange("editSupplier", e.target.value)
                        }
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Nº Factura *
                      </label>
                      <Input
                        value={ff.editInvoiceNumber || ""}
                        onChange={(e) =>
                          fieldChange("editInvoiceNumber", e.target.value)
                        }
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        NIF
                      </label>
                      <Input
                        value={ff.editSupplierTaxId || ""}
                        onChange={(e) =>
                          fieldChange("editSupplierTaxId", e.target.value)
                        }
                        placeholder="B12345678"
                        className="h-8 text-xs font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* VAT lines — multi-row, igual que en facturas reconocidas */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Líneas de IVA
                    </h3>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={addVatLine}
                      className="h-7 px-2 text-[11px] gap-1"
                    >
                      <Plus className="h-3 w-3" /> Añadir línea
                    </Button>
                  </div>

                  <div className="rounded-md border divide-y">
                    <div className="grid grid-cols-[1fr_60px_1fr_1.2fr_28px] gap-2 px-2 py-1.5 bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                      <span>Base imponible</span>
                      <span className="text-center">% IVA</span>
                      <span>Cuota IVA</span>
                      <span>Cta. gasto</span>
                      <span />
                    </div>
                    {vatLines.map((line, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-[1fr_60px_1fr_1.2fr_28px] gap-2 px-2 py-1.5 items-center"
                      >
                        <NumericInput
                          value={line.taxableBase}
                          onChange={(v) => updateVatLine(idx, { taxableBase: v })}
                          placeholder="0.00"
                          className="h-8 text-xs"
                        />
                        <NumericInput
                          value={line.vatRate}
                          onChange={(v) => updateVatLine(idx, { vatRate: v })}
                          placeholder="21"
                          className="h-8 text-xs text-center"
                        />
                        <NumericInput
                          value={line.vatAmount}
                          onChange={(v) => updateVatLine(idx, { vatAmount: v })}
                          placeholder="0.00"
                          className="h-8 text-xs"
                        />
                        <Input
                          value={line.expenseAccount || ""}
                          onChange={(e) => updateVatLine(idx, { expenseAccount: e.target.value })}
                          placeholder="6xxxxxxx"
                          className="h-8 text-xs font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => removeVatLine(idx)}
                          disabled={vatLines.length <= 1}
                          className="flex items-center justify-center rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                          title="Eliminar línea"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <div className="grid grid-cols-[1fr_60px_1fr_1.2fr_28px] gap-2 px-2 py-1.5 text-[11px] text-muted-foreground bg-muted/20">
                      <span className="font-medium text-right">
                        Base total: {formatNumber(totalsPreview.base)} €
                      </span>
                      <span />
                      <span className="font-medium">
                        Cuota total: {formatNumber(totalsPreview.vat)} €
                      </span>
                      <span />
                      <span />
                    </div>
                  </div>

                </div>

                {/* IRPF, Total, Forma de pago */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    IRPF y total
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        % IRPF
                      </label>
                      <Input
                        value={ff.editIrpfRate || ""}
                        onChange={(e) =>
                          fieldChange("editIrpfRate", e.target.value)
                        }
                        placeholder="0"
                        className="h-8 text-xs"
                        type="number"
                        step="0.01"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Cuota IRPF
                      </label>
                      <Input
                        value={ff.editIrpfAmount || ""}
                        onChange={(e) =>
                          fieldChange("editIrpfAmount", e.target.value)
                        }
                        placeholder="0.00"
                        className="h-8 text-xs"
                        type="number"
                        step="0.01"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Total
                      </label>
                      <Input
                        value={ff.editTotalAmount || ""}
                        onChange={(e) =>
                          fieldChange("editTotalAmount", e.target.value)
                        }
                        placeholder="0.00"
                        className="h-8 text-xs"
                        type="number"
                        step="0.01"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        F. Pago
                      </label>
                      <Input
                        value={ff.editPaymentMethod || ""}
                        onChange={(e) =>
                          fieldChange("editPaymentMethod", e.target.value)
                        }
                        placeholder="CAJA, TARJETA…"
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                </div>

                {/* Cuenta proveedor y descripción */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Cuenta proveedor y descripción
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Cuenta proveedor
                      </label>
                      <Input
                        value={ff.editSupplierAccount || ""}
                        onChange={(e) => fieldChange("editSupplierAccount", e.target.value)}
                        placeholder="41000000"
                        className="h-8 text-xs font-mono"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Descripción
                      </label>
                      <Input
                        value={ff.editDescription || ""}
                        onChange={(e) => fieldChange("editDescription", e.target.value)}
                        placeholder="Concepto del asiento"
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                </div>




                <div className="flex justify-end gap-2 pt-2 border-t">
                  {onDiscard && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { onDiscard(ff.id); onClose(); }}
                      className="h-8 px-3 text-xs gap-1 mr-auto hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30"
                      title="Enviar a la papelera"
                    >
                      <Trash2 className="h-3 w-3" /> <span>Desechar</span>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="h-8 px-3 text-xs"
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      onValidate(ff, vatLines);
                      onClose();
                    }}
                    disabled={
                      !ff.editDate || !ff.editSupplier || !ff.editInvoiceNumber
                    }
                    className="h-8 px-3 text-xs bg-[hsl(var(--brand-green))] hover:bg-[hsl(152,40%,45%)] text-white"
                  >
                    <Check className="h-3 w-3 mr-1" /> Validar
                  </Button>
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FailedFileSplitView;
